import { useCallback, useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { X } from 'lucide-react';
import { Button } from './Button';

/**
 * Overlay plumbing, shared by Modal and Drawer.
 *
 * Both are real dialogs: the page behind them stops scrolling, focus moves inside and
 * cannot leave, Escape closes, and focus returns to whatever opened them. Written
 * once here rather than twice, and not delegated to a dependency for two components.
 */

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const { body } = document;
    const previousOverflow = body.style.overflow;
    const previousPadding = body.style.paddingRight;
    // Compensate for the scrollbar so the page behind does not jump sideways.
    const gap = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = 'hidden';
    if (gap > 0) body.style.paddingRight = `${gap}px`;
    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPadding;
    };
  }, [active]);
}

/**
 * The traps open now, oldest first. Only the topmost answers Escape and Tab:
 * every trap listens on `document`, and a dialog opened over a drawer (a
 * flyer over the announcement it is being edited in, 3 Oct 2026) closed both
 * on one Escape — and the drawer's unsaved edits with it.
 */
const openTraps: symbol[] = [];

export function useFocusTrap(
  active: boolean,
  containerRef: RefObject<HTMLElement | null>,
  onClose: () => void,
): void {
  // The close handler is read through a ref: callers pass a fresh arrow on
  // every render, and with it in the effect's dependencies the trap re-armed
  // on each keystroke — refocusing the panel's first control and pulling the
  // caret out of the field being typed in (8 Sep 2026: "the cursor moves
  // after every letter").
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!active) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const container = containerRef.current;
    const self = Symbol('trap');
    openTraps.push(self);

    const focusFirst = () => {
      if (!container) return;
      const target =
        container.querySelector<HTMLElement>('[data-autofocus]') ??
        container.querySelector<HTMLElement>(FOCUSABLE) ??
        container;
      target.focus({ preventScroll: true });
    };
    // Wait a frame: the panel is animating in and may not be measurable yet.
    const raf = window.requestAnimationFrame(focusFirst);

    const onKeyDown = (event: KeyboardEvent) => {
      // A dialog over this one has the keys.
      if (openTraps[openTraps.length - 1] !== self) return;
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !container) return;

      const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (element) => element.offsetParent !== null || element === document.activeElement,
      );
      if (focusable.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown, true);
      const at = openTraps.indexOf(self);
      if (at !== -1) openTraps.splice(at, 1);
      previouslyFocused?.focus?.({ preventScroll: true });
    };
  }, [active, containerRef]);
}

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZES: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  size?: ModalSize;
  children: ReactNode;
  /** Buttons for the footer. Primary action last, as the platform expects. */
  footer?: ReactNode;
  /** Blocks the backdrop click — for a form with unsaved input. */
  dismissible?: boolean;
  className?: string;
}

/** A centred dialog for a decision or a short form. */
export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  children,
  footer,
  dismissible = true,
  className,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => {
    if (dismissible) onClose();
  }, [dismissible, onClose]);

  useScrollLock(open);
  useFocusTrap(open, panelRef, close);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-100 flex items-end justify-center sm:items-center print-hide">
      <div
        aria-hidden
        onClick={close}
        className="absolute inset-0 bg-[color-mix(in_oklab,var(--c-ink)_45%,transparent)] backdrop-blur-[2px] animate-fade-in"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={clsx(
          'relative z-10 flex max-h-[92vh] w-full flex-col rounded-t-lg bg-surface shadow-float sm:rounded-lg',
          'animate-slide-up',
          SIZES[size],
          className,
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2 className="font-display text-input font-semibold text-ink">{title}</h2>
            {description ? <p className="mt-0.5 text-micro text-muted">{description}</p> : null}
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            onClick={onClose}
            aria-label="Close"
            className="-mt-1 -mr-1.5"
          >
            <X className="h-4 w-4" aria-hidden />
          </Button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer ? (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3.5">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

export interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
}

/** Yes or no, for anything that cannot be undone. */
export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  loading = false,
}: ConfirmModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      dismissible={!loading}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={loading}
            data-autofocus
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-micro leading-relaxed text-ink-2">{message}</p>
    </Modal>
  );
}
